import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Globe, Link2, Shield, Zap, TrendingUp, Clock, Sparkles, Rocket, Target, Send, Calendar } from "lucide-react";
import { useTheme, logoOptions } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import api from "../lib/api";
import { safeExternalHref } from "../lib/utils";

// Dynamic Logo Component
const RootSeoLogo = ({ size = "default" }) => {
  const { getLogo } = useTheme();
  const logo = getLogo();
  const sizeClasses = {
    small: "w-8 h-8",
    default: "w-12 h-12",
    large: "w-16 h-16"
  };
  
  return (
    <div className={`${sizeClasses[size]} relative group`}>
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-secondary rounded-full opacity-90 group-hover:opacity-100 transition-opacity" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-full blur-md opacity-50 group-hover:opacity-70 transition-opacity" />
      <div 
        className="relative w-full h-full flex items-center justify-center text-black p-2 rounded-full overflow-hidden"
        dangerouslySetInnerHTML={{ __html: logo.svg }}
      />
    </div>
  );
};

const LandingPage = () => {
  const [stats, setStats] = useState(() => {
    try {
      const raw = localStorage.getItem("rootseo_public_stats");
      return raw ? JSON.parse(raw) : { total_sites: 0, total_backlinks: 0, active_backlinks: 0 };
    } catch {
      return { total_sites: 0, total_backlinks: 0, active_backlinks: 0 };
    }
  });
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const { currentTheme, siteContent } = useTheme();
  const { t, getFeatures, language } = useLanguage();

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get("/public/stats");
      setStats(response.data);
      localStorage.setItem("rootseo_public_stats", JSON.stringify(response.data));
    } catch (error) {
      console.log("Stats fetch error, using defaults");
    }
  }, []);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await api.get("/public/packages");
      setPackages(response.data || []);
    } catch (error) {
      console.log("Packages fetch error, using defaults");
      // Fallback to default packages if API fails
      setPackages([
        { name: language === "tr" ? "Başlangıç" : "Starter", credits: 10, price: 10, bonus_credits: 0, template: "default", is_featured: false },
        { name: language === "tr" ? "Profesyonel" : "Professional", credits: 50, price: 40, bonus_credits: 10, template: "popular", is_featured: true },
        { name: "Enterprise", credits: 200, price: 140, bonus_credits: 60, template: "premium", is_featured: false }
      ]);
    } finally {
      setLoadingPackages(false);
    }
  }, [language]);

  useEffect(() => {
    fetchStats();
    fetchPackages();
  }, [fetchPackages, fetchStats]);
  
  // Feature icons mapping
  const featureIcons = [Globe, Zap, Shield, TrendingUp, Target, Sparkles];
  
  // Always use language-based features for proper translation
  const features = getFeatures();

  return (
    <div className="min-h-screen overflow-x-hidden grid-texture" data-theme={currentTheme}>
      {/* Hero Section */}
      <div className="relative">
        
        {/* Content */}
        <div className="relative z-10">
          {/* Header */}
          <nav className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 flex items-center justify-between overflow-hidden">
            <div className="flex items-center gap-2 flex-shrink-0">
              <RootSeoLogo size="small" />
              <div className="sparkle-wrapper hidden xs:inline">
                <span className="sparkle"></span>
                <span className="sparkle"></span>
                <span className="sparkle"></span>
                <span className="sparkle"></span>
                <span className="glow-credit font-mono font-bold text-sm sm:text-xl tracking-normal">ROOT-SEO</span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>
              <Link to="/login">
                <Button variant="ghost" className="font-mono text-xs sm:text-sm hover:text-primary px-2 sm:px-4 h-8 sm:h-10" data-testid="login-btn">
                  {t("nav.login")}
                </Button>
              </Link>
              <Link to="/register">
                <Button className="font-mono text-xs sm:text-sm bg-primary text-black hover:bg-primary/90 rounded-none px-2 sm:px-4 h-8 sm:h-10" data-testid="register-btn">
                  {t("nav.register")}
                </Button>
              </Link>
            </div>
          </nav>

          {/* Hero Content */}
          <div className="container mx-auto px-4 pt-12 sm:pt-20 pb-20 sm:pb-32 overflow-hidden">
            <div className="max-w-5xl mx-auto text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full mb-8 animate-fade-in-up">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-mono text-muted-foreground">
                  {language === "tr" ? "Yeni Nesil SEO Hizmetleri" : "Next Generation SEO Services"}
                </span>
              </div>

              {/* Main Title */}
              <h1 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-mono font-bold tracking-tighter uppercase mb-6 animate-fade-in-up px-2" style={{ animationDelay: "0.1s" }} data-testid="hero-title">
                {language === "tr" ? "SEO PERFORMANSINIZI" : "BOOST YOUR SEO"}<br />
                <span className="gradient-text text-glow">{language === "tr" ? "ŞİMDİ ARTIRIN" : "PERFORMANCE"}</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto font-body animate-fade-in-up px-4" style={{ animationDelay: "0.2s" }}>
                {language === "tr" 
                  ? "Ücretsiz kayıt olun ve sitelerimizi inceleyin. Kredi satın alarak hemen başlayın."
                  : "Sign up for free and explore our sites. Get started by purchasing credits."}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up px-4" style={{ animationDelay: "0.3s" }}>
                <Link to="/register" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto font-mono uppercase tracking-wider bg-primary text-black hover:bg-primary/90 rounded-none px-6 sm:px-10 py-6 text-base sm:text-lg glow-primary" data-testid="get-started-btn">
                    <Rocket className="w-5 h-5 mr-2" />
                    {t("hero.cta")}
                  </Button>
                </Link>
                <Link to="/login" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto font-mono uppercase tracking-wider rounded-none px-6 sm:px-10 py-6 text-base sm:text-lg border-white/20 hover:bg-white/5" data-testid="view-sites-btn">
                    {language === "tr" ? "Siteleri İncele" : "View Sites"}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="relative z-10 -mt-10 sm:-mt-20">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 max-w-4xl mx-auto">
            {[
              { value: stats.total_sites > 0 ? `${stats.total_sites}+` : "500+", label: language === "tr" ? "AKTİF SİTE" : "ACTIVE SITES", icon: Globe },
              { value: stats.total_backlinks > 0 ? `${stats.total_backlinks > 1000 ? Math.floor(stats.total_backlinks/1000) + 'K' : stats.total_backlinks}+` : "1K+", label: language === "tr" ? "BAŞARILI SİPARİŞ" : "COMPLETED ORDERS", icon: Rocket },
              { value: "99%", label: "UPTIME", icon: Shield },
              { value: "24/7", label: language === "tr" ? "DESTEK" : "SUPPORT", icon: Clock }
            ].map((stat, i) => (
              <div 
                key={i} 
                className="glass-strong p-3 sm:p-6 flex flex-col items-center justify-center text-center group hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 animate-fade-in-up"
                style={{ animationDelay: `${0.4 + i * 0.1}s` }}
              >
                <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary mb-1 sm:mb-2 group-hover:scale-110 transition-transform" />
                <div className="text-xl sm:text-3xl md:text-4xl font-mono font-bold text-primary mb-1 sm:mb-2">{stat.value}</div>
                <div className="text-[10px] sm:text-xs font-mono tracking-wider text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="relative py-16 sm:py-32">
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-mono font-bold tracking-tight uppercase mb-4">
              {language === "tr" ? "Neden" : "Why"} <span className="gradient-text">ROOT-SEO</span>?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === "tr" 
                ? "En gelişmiş SEO araçları ve kredi bazlı esnek ödeme sistemi"
                : "Most advanced SEO tools and flexible credit-based payment system"}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {features.map((feature, i) => {
              const IconComponent = featureIcons[i % featureIcons.length];
              return (
                <div 
                  key={i} 
                  className="glass p-8 group hover:border-primary/50 transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <IconComponent className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-mono font-bold text-lg mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground font-body">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pricing Preview */}
      <div className="relative py-16 sm:py-32">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-mono font-bold tracking-tight uppercase mb-4">
              {language === "tr" ? "KREDİ & ABONELİK" : "CREDITS & SUBSCRIPTION"} <span className="gradient-text">{language === "tr" ? "PAKETLERİ" : "PACKAGES"}</span>
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto px-4">
              {language === "tr" 
                ? "İhtiyacınıza uygun paketi seçin, hemen kullanmaya başlayın"
                : "Choose the package that suits your needs, start using immediately"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto px-2">
            {loadingPackages ? (
              <div className="col-span-3 text-center text-muted-foreground py-8">
                {language === "tr" ? "Paketler yükleniyor..." : "Loading packages..."}
              </div>
            ) : packages.length === 0 ? (
              <div className="col-span-3 text-center text-muted-foreground py-8">
                {language === "tr" ? "Henüz paket bulunmuyor" : "No packages available"}
              </div>
            ) : (
              packages.map((pkg) => {
                // Template class belirleme
                const getTemplateClass = () => {
                  if (pkg.is_featured || pkg.template === "popular") return "package-popular";
                  if (pkg.template === "premium") return "package-premium";
                  if (pkg.template === "gold") return "package-gold";
                  if (pkg.template === "silver") return "package-silver";
                  if (pkg.template === "platinum") return "package-platinum";
                  if (pkg.template === "startup") return "package-startup";
                  if (pkg.template === "agency") return "package-agency";
                  if (pkg.template === "unlimited") return "package-unlimited";
                  return "package-default";
                };

                // Badge bilgisi
                const getBadge = () => {
                  if (pkg.is_featured || pkg.template === "popular") return { text: t("pricing.popular"), color: "bg-primary text-black" };
                  if (pkg.template === "premium") return { text: "PREMIUM", color: "bg-purple-500 text-white" };
                  if (pkg.template === "gold") return { text: language === "tr" ? "ALTIN" : "GOLD", color: "bg-gradient-to-r from-yellow-400 to-yellow-600 text-black" };
                  if (pkg.template === "silver") return { text: language === "tr" ? "GÜMÜŞ" : "SILVER", color: "bg-gradient-to-r from-gray-300 to-gray-400 text-black" };
                  if (pkg.template === "platinum") return { text: language === "tr" ? "PLATİN" : "PLATINUM", color: "bg-gradient-to-r from-slate-200 via-blue-200 to-slate-300 text-black" };
                  if (pkg.template === "startup") return { text: "STARTUP", color: "bg-blue-500 text-white" };
                  if (pkg.template === "agency") return { text: language === "tr" ? "AJANS" : "AGENCY", color: "bg-orange-500 text-white" };
                  if (pkg.template === "unlimited") return { text: language === "tr" ? "SINIRSIZ" : "UNLIMITED", color: "bg-gradient-to-r from-pink-500 to-purple-500 text-white" };
                  return null;
                };

                const badge = getBadge();

                return (
                  <div 
                    key={pkg.id} 
                    className={`package-shell h-full p-6 sm:p-8 relative text-center flex flex-col ${getTemplateClass()}`}
                  >
                    {badge && (
                      <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 text-xs font-mono font-bold whitespace-nowrap z-10 ${badge.color}`}>
                        {badge.text}
                      </div>
                    )}
                    <h3 className="font-mono font-bold text-lg sm:text-xl mb-2 mt-2">
                      {language === "tr" && pkg.name_tr ? pkg.name_tr : (language === "en" && pkg.name_en ? pkg.name_en : pkg.name)}
                    </h3>
                    {(pkg.description || pkg.description_tr || pkg.description_en) && (
                      <p className="text-sm text-muted-foreground mb-3 min-h-[2.5rem]">
                        {language === "tr" ? (pkg.description_tr || pkg.description) : (pkg.description_en || pkg.description)}
                      </p>
                    )}
                    <div className="flex items-baseline justify-center gap-1 mb-4">
                      <span className="text-3xl sm:text-4xl font-mono font-bold text-primary">${pkg.price}</span>
                    </div>
                    <div className="space-y-2 mb-6 flex-1">
                      {pkg.product_type === "subscription" ? (
                        <>
                          <div className="flex items-center justify-center gap-2 text-sm">
                            <span className="text-xl font-mono font-bold text-primary">∞</span>
                            <span>{language === "tr" ? "Sınırsız kredi" : "Unlimited credits"}</span>
                          </div>
                          {pkg.days > 0 && (
                            <div className="flex items-center justify-center gap-2 text-sm text-primary">
                              <Calendar className="w-4 h-4" />
                              <span>{pkg.days} {language === "tr" ? "gün abonelik" : "days subscription"}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex items-center justify-center gap-2 text-sm">
                            <Sparkles className="w-4 h-4 text-primary" />
                            <span>{pkg.credits} {t("pricing.credits")}</span>
                          </div>
                          {pkg.bonus_credits > 0 && (
                            <div className="flex items-center justify-center gap-2 text-sm text-primary">
                              <Zap className="w-4 h-4" />
                              <span>+{pkg.bonus_credits} {t("pricing.bonus")}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <Link to="/register" className="block mt-auto">
                      <Button className={`w-full font-mono rounded-none ${pkg.is_featured ? "bg-primary text-black hover:bg-primary/90" : "bg-primary text-black hover:bg-primary/90"}`}>
                        {t("pricing.buy")}
                      </Button>
                    </Link>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-16 sm:py-32">
        <div className="container mx-auto px-2 sm:px-4 relative z-10">
          <div className="glass-strong p-6 sm:p-12 md:p-20 text-center max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-mono font-bold tracking-tight uppercase mb-4 sm:mb-6">
              {language === "tr" ? "SEO PERFORMANSINIZI" : "BOOST YOUR SEO"}<br />
              <span className="gradient-text">{language === "tr" ? "ŞİMDİ ARTIRIN" : "PERFORMANCE NOW"}</span>
            </h2>
            <p className="text-sm sm:text-lg text-muted-foreground mb-6 sm:mb-10 max-w-xl mx-auto px-2">
              {language === "tr" 
                ? "Ücretsiz kayıt olun ve sitelerimizi inceleyin. Kredi satın alarak hemen başlayın."
                : "Register for free and browse our sites. Buy credits and start immediately."}
            </p>
            <Link to="/register" className="inline-block w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto font-mono uppercase tracking-wider bg-primary text-black hover:bg-primary/90 rounded-none px-6 sm:px-12 py-4 sm:py-6 text-sm sm:text-lg glow-primary" data-testid="cta-register-btn">
                <Rocket className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {language === "tr" ? "Ücretsiz Kayıt Ol" : "Register Free"}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 relative z-10">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <RootSeoLogo size="small" />
            <div className="sparkle-wrapper">
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="glow-credit font-mono font-bold tracking-normal">ROOT-SEO</span>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
            {safeExternalHref(siteContent?.footer?.telegramLink) && (
              <a 
                href={safeExternalHref(siteContent.footer.telegramLink)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:text-primary/80 font-mono text-sm transition-colors"
              >
                <Send className="w-4 h-4" />
                {t("footer.telegram")}
              </a>
            )}
            <p className="text-sm text-muted-foreground font-mono">
              © {new Date().getFullYear()} ROOT-SEO {t("footer.rights")}
            </p>
            <div className="sparkle-wrapper">
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="glow-credit text-sm font-mono font-bold">
                Moderated by AnubisROOT-NoxShell
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
